import { useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { safeCastPlan } from '@/types/user';

interface RealTimeUpdateProps {
  onProfileUpdate?: (payload: any) => void;
  onContentUpdate?: (payload: any) => void;
  onUsageUpdate?: (payload: any) => void;
}

export const useRealTimeUpdates = ({
  onProfileUpdate,
  onContentUpdate,
  onUsageUpdate
}: RealTimeUpdateProps = {}) => {
  const { user, updateUser } = useAuth();

  const handleProfileChange = useCallback((payload: any) => {
    console.log('Profile update received:', payload);
    
    if (payload.new && user?.id === payload.new.user_id) {
      // Update user context with new profile data
      updateUser({
        plan: payload.new.plan,
        creditsUsed: payload.new.credits_used,
        creditsLimit: payload.new.credits_limit
      });
      
      // Call custom handler if provided
      onProfileUpdate?.(payload);
    }
  }, [user?.id, updateUser, onProfileUpdate]);

  const handleContentChange = useCallback((payload: any) => {
    console.log('Content update received:', payload);
    
    if (payload.new && user?.id === payload.new.user_id) {
      // Call custom handler if provided
      onContentUpdate?.(payload);
    }
  }, [user?.id, onContentUpdate]);

  const handleUsageChange = useCallback((payload: any) => {
    console.log('Usage update received:', payload);
    
    if (payload.new && user?.id === payload.new.user_id) {
      // Update user context with new usage data
      updateUser({
        plan: payload.new.plan
      });
      
      // Call custom handler if provided
      onUsageUpdate?.(payload);
    }
  }, [user?.id, updateUser, onUsageUpdate]);

  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to profile changes
    const profileChannel = supabase
      .channel('profile-changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        handleProfileChange
      )
      .subscribe();

    // Subscribe to content generation updates
    const contentChannel = supabase
      .channel('content-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'generated_content',
          filter: `user_id=eq.${user.id}`
        },
        handleContentChange
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'generated_content',
          filter: `user_id=eq.${user.id}`
        },
        handleContentChange
      )
      .subscribe();

    console.log('Real-time subscriptions established for user:', user.id);

    return () => {
      console.log('Cleaning up real-time subscriptions');
      supabase.removeChannel(profileChannel);
      supabase.removeChannel(contentChannel);
    };
  }, [user?.id, handleProfileChange, handleContentChange, handleUsageChange]);

  // Manual sync function for immediate updates
  const syncUserData = useCallback(async () => {
    if (!user?.id) return;

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error syncing user data:', error);
        return;
      }

      if (profile) {
        updateUser({
          plan: safeCastPlan(profile.plan)
        });
      }
    } catch (error) {
      console.error('Error in syncUserData:', error);
    }
  }, [user?.id, updateUser]);

  return {
    syncUserData
  };
};